import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FaCheck } from "react-icons/fa";
import Tracking from "src/react/components/tracking";
import { color, fakeFocus } from "src/styles/variables";

const CHECKBOX_DEFAULT_SIZE = 28;

export class Checkbox extends Component {
  static defaultProps = {
    disabled: false,
    checked: false,
    size: CHECKBOX_DEFAULT_SIZE,
  };

  static propTypes = {
    children: PropTypes.any.isRequired,
    disabled: PropTypes.bool,
    checked: PropTypes.bool,
    id: PropTypes.string.isRequired,
    size: PropTypes.number,
    css: PropTypes.string,
    required: PropTypes.bool,
    variant: PropTypes.string,
    tracking: PropTypes.string,
    trackingOptions: PropTypes.object,
  };

  render() {
    const {
      checked,
      id,
      children,
      css,
      disabled,
      size,
      tracking,
      trackingOptions,
      variant,
      ...props
    } = this.props;
    return (
      <CheckboxLabel
        htmlFor={id}
        id={`${id}-label`}
        css={css}
        data-selector={`CheckboxInput-${id}`}
        disabled={disabled}
        checked={checked}
        variant={variant}
      >
        <Tracking tracking={tracking} trackingOptions={trackingOptions}>
          <CheckboxInput
            id={id}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            {...props}
          />
        </Tracking>
        <CheckboxCheck
          disabled={disabled}
          checked={checked}
          size={size}
          variant={variant}
        >
          {checked && (
            <CheckboxCheckIcon
              disabled={disabled}
              size={size}
              variant={variant}
            />
          )}
        </CheckboxCheck>
        <CheckboxContent>{children}</CheckboxContent>
      </CheckboxLabel>
    );
  }
}

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  position: relative;
  line-height: 20px;
  cursor: pointer;
  font-weight: 400;
  ${({ disabled }) =>
    disabled
      ? `color: ${color.dustyGrey}; cursor: not-allowed;`
      : `color: ${color.grey};`};
  ${({ variant }) =>
    variant === "boxed" &&
    `
    color: ${color.white};
    padding: 5px;
    border: 2px solid ${color.emperor};
    border-radius: 5px;
    background-color: ${color.emperor};
    font-size: 14px;
  `};
  ${({ variant, checked }) =>
    variant === "boxed" &&
    checked &&
    `
    border-color: ${color.mineShaft};
    background-color: ${color.mineShaft};
      `};
  ${({ css }) => css};
`;

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  :focus + div {
    ${fakeFocus};
  }
`;

const CheckboxContent = styled.div`
  display: flex;
  align-items: center;
`;

const CheckboxCheck = styled.div.attrs({
  role: "checkbox",
})`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  border: 2px solid ${color.midGrey};
  border-radius: 5px;
  margin-right: 10px;
  flex-shrink: 0;
  ${({ size }) => `
      height: ${size}px;
      width: ${size}px;
      `};
  ${({ variant }) =>
    variant === "boxed" &&
    `
        background-color: ${color.white};
      `};
  ${({ variant, checked }) =>
    variant === "boxed" &&
    !checked &&
    `
        border-color: ${color.emperor};
      `};
  ${({ variant, checked }) =>
    variant === "boxed" &&
    checked &&
    `
        border-color: ${color.mineShaft};
      `};
  ${({ checked, variant }) =>
    checked && !variant && `border-color: ${color.green};`};
  ${({ required, variant }) =>
    required && !variant && `border-color: ${color.torchRed};`};
  ${({ disabled }) =>
    disabled &&
    `
    border-color: ${color.dustyGrey};
    color: ${color.dustyGrey};
    cursor: not-allowed;
  `};
`;

const CheckboxCheckIcon = styled(FaCheck)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  fill: ${color.green};
  width: 70%;
  ${({ variant }) =>
    variant === "boxed" &&
    `
    fill: ${color.mineShaft};
      `};
  ${({ disabled }) => disabled && `fill: ${color.dustyGrey};`};
`;

export class CheckboxFormik extends Component {
  render() {
    const {
      field: { name },
      form: { errors, touched } = { errors: {}, touched: {} },
      label = this.props.value || "",
      ...rest
    } = this.props;

    const isRequired = touched[name] != null && errors[name] != null;
    const inputProps = {
      isRequired,
      ...this.props.field,
      ...rest,
    };

    return <Checkbox {...inputProps}>{label}</Checkbox>;
  }
}
